<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useConnectivity } from '@/composition/use-connectivity';
  import { useOfflineStore } from '@/stores/offline.store';

  const { status } = useConnectivity();
  const offlineStore = useOfflineStore();

  const isOpen = ref(false);

  // Inert `queue: []` when offline ordering is disabled, so always safe.
  const queuedCount = computed(() => offlineStore.queue?.length ?? 0);

  const labelKey = computed<string | null>(() => {
    switch (status.value) {
      case 'reconnecting':
        return 'connectivity.chipReconnecting';
      case 'offline':
        return 'connectivity.chipOffline';
      case 'restored':
        return 'connectivity.chipOnline';
      default:
        return null;
    }
  });

  const panelTitleKey = computed(() => {
    switch (status.value) {
      case 'reconnecting':
        return 'connectivity.chipReconnecting';
      case 'offline':
        return 'connectivity.chipOffline';
      case 'restored':
        return 'connectivity.restoredTitle';
      default:
        return 'connectivity.onlineTitle';
    }
  });

  const panelDescKey = computed(() => {
    switch (status.value) {
      case 'reconnecting':
        return 'connectivity.reconnectingDesc';
      case 'offline':
        return 'connectivity.offlineDesc';
      case 'restored':
        return 'connectivity.restoredDesc';
      default:
        return 'connectivity.onlineDesc';
    }
  });
</script>

<template>
  <!-- is-idle: online with nothing queued (the bar may hide it on phones). -->
  <div
    class="connectivity-chip"
    :class="{ 'is-idle': !labelKey && queuedCount === 0 }"
  >
    <v-menu v-model="isOpen" bottom offset-y :close-on-content-click="false">
      <template v-slot:activator="{ on, attrs }">
        <button
          class="chip-button"
          :class="{ 'has-label': labelKey }"
          type="button"
          :aria-label="$t('connectivity.chipAria')"
          v-bind="attrs"
          v-on="on"
        >
          <span class="chip-dot" :class="`is-${status}`"></span>
          <span v-if="labelKey" class="chip-label">{{ $t(labelKey) }}</span>
        </button>
      </template>
      <div class="chip-panel">
        <div class="panel-title">
          <span class="panel-dot" :class="`is-${status}`"></span>
          <span>{{ $t(panelTitleKey) }}</span>
        </div>
        <p class="panel-desc">{{ $t(panelDescKey) }}</p>
        <div v-if="queuedCount > 0" class="panel-queued">
          {{ $tc('connectivity.queuedActions', queuedCount) }}
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style scoped lang="scss">
  @keyframes chip-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }

  .connectivity-chip {
    @apply tw-flex tw-items-center;

    .chip-button {
      @apply tw-flex tw-h-11 tw-min-w-[44px] tw-items-center tw-justify-center tw-rounded-full tw-px-2;

      &.has-label {
        @apply tw-h-8 tw-bg-neutral-100;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .chip-dot {
        @apply tw-h-2 tw-w-2 tw-rounded-full;

        &.is-online,
        &.is-restored {
          @apply tw-bg-emerald-500;
        }

        &.is-reconnecting {
          @apply tw-bg-amber-500;
          animation: chip-pulse 1.2s ease-in-out infinite;
        }

        &.is-offline {
          @apply tw-bg-red-500;
          animation: chip-pulse 1.2s ease-in-out infinite;
        }
      }

      .chip-label {
        @apply tw-ml-1.5 tw-text-sm tw-font-medium tw-text-neutral-700;

        @media (max-width: 599px) {
          display: none;
        }
      }
    }
  }

  .chip-panel {
    @apply tw-flex tw-min-w-[240px] tw-max-w-[280px] tw-flex-col tw-bg-surface tw-p-4;

    .panel-title {
      @apply tw-flex tw-items-center tw-text-sm tw-font-semibold tw-text-neutral-800;

      .panel-dot {
        @apply tw-mr-2 tw-h-2 tw-w-2 tw-rounded-full;

        &.is-online,
        &.is-restored {
          @apply tw-bg-emerald-500;
        }

        &.is-reconnecting {
          @apply tw-bg-amber-500;
        }

        &.is-offline {
          @apply tw-bg-red-500;
        }
      }
    }

    .panel-desc {
      @apply tw-mb-0 tw-mt-2 tw-text-sm tw-text-neutral-500;
    }

    .panel-queued {
      @apply tw-mt-2 tw-rounded-lg tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-amber-700;
    }
  }
</style>
